/**
 * Report Execution Engine
 *
 * Handles all report execution logic including:
 * - Query execution and data retrieval
 * - Filter application (8 operators)
 * - Sorting and grouping
 * - Aggregations (count, sum, avg, min, max)
 * - Result formatting
 */

import { ListViewRecord } from '../types/listView';

export interface ReportConfiguration {
  id: string;
  name: string;
  objectName: string;
  selectedFields: string[]; // Field API names to include
  filters?: ReportFilter[];
  groupBy?: string[]; // Fields to group by
  sortBy?: ReportSort[];
  chartType?: 'table' | 'bar' | 'line' | 'pie' | 'summary';
  aggregations?: ReportAggregation[];
}

export interface ReportFilter {
  field: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'startsWith' | 'greaterThan' | 'lessThan' | 'between' | 'in';
  value: any;
  value2?: any; // For 'between' operator
}

export interface ReportSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ReportAggregation {
  field: string;
  function: 'count' | 'sum' | 'avg' | 'min' | 'max';
  label: string;
}

export interface ReportResults {
  columns: ReportColumn[];
  rows: any[];
  summary?: ReportSummary;
  totalRecords: number;
  executedAt: string;
}

export interface ReportColumn {
  field: string;
  label: string;
  type: string;
}

export interface ReportSummary {
  [key: string]: {
    label: string;
    value: any;
    function: string;
  };
}

/**
 * Execute a report and return results
 */
export async function executeReport(
  config: ReportConfiguration,
  records: ListViewRecord[]
): Promise<ReportResults> {
  console.log('=== REPORT EXECUTION DEBUG ===');
  console.log('Report:', config.name);
  console.log('Object:', config.objectName);
  console.log('Total records available:', records.length);
  console.log('Selected fields:', config.selectedFields);
  console.log('Filters:', config.filters);
  console.log('Group by:', config.groupBy);
  console.log('Aggregations:', config.aggregations);

  // Log first record to see available fields
  if (records.length > 0) {
    console.log('Sample record fields:', Object.keys(records[0]));
    console.log('Sample record:', records[0]);
  } else {
    console.warn('⚠️ No records available for this object!');
    return {
      columns: [],
      rows: [],
      totalRecords: 0,
      executedAt: new Date().toISOString()
    };
  }

  // Validate selected fields exist in records
  const availableFields = Object.keys(records[0]);
  const missingFields = config.selectedFields.filter(f => !availableFields.includes(f));

  if (missingFields.length > 0) {
    console.warn('⚠️ Missing fields in data:', missingFields);
    console.warn('📋 Available fields:', availableFields);
  }

  // Filter out missing fields from config
  const validFields = config.selectedFields.filter(f => availableFields.includes(f));

  if (validFields.length === 0) {
    console.error('❌ No valid fields found! Report cannot execute.');
    console.error('Requested fields:', config.selectedFields);
    console.error('Available fields:', availableFields);
    return {
      columns: [],
      rows: [],
      totalRecords: 0,
      executedAt: new Date().toISOString()
    };
  }

  if (validFields.length < config.selectedFields.length) {
    console.log('✅ Using valid fields only:', validFields);
  }

  // Use only valid fields
  const validConfig = { ...config, selectedFields: validFields };

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  // Step 1: Apply filters
  let filteredRecords = applyFilters(records, config.filters || []);
  console.log('After filters:', filteredRecords.length, 'records');

  // Step 2: Apply sorting
  if (config.sortBy && config.sortBy.length > 0) {
    filteredRecords = applySorting(filteredRecords, config.sortBy);
  }

  // Step 3: Apply grouping and aggregation
  let results: any[];
  let summary: ReportSummary | undefined;

  if (validConfig.groupBy && validConfig.groupBy.length > 0) {
    // Grouped report with aggregations
    const grouped = applyGrouping(filteredRecords, validConfig.groupBy, validConfig.aggregations || []);
    results = grouped.rows;
    summary = grouped.summary;
  } else {
    // Detail report - just select fields
    results = filteredRecords.map(record => {
      const row: any = {};
      validConfig.selectedFields.forEach(field => {
        row[field] = record[field];
      });
      return row;
    });

    // Calculate summary aggregations
    if (validConfig.aggregations && validConfig.aggregations.length > 0) {
      summary = calculateSummary(filteredRecords, validConfig.aggregations);
    }
  }

  // Step 4: Build column definitions
  const columns: ReportColumn[] = validConfig.selectedFields.map(field => ({
    field,
    label: formatFieldLabel(field),
    type: inferFieldType(field, filteredRecords)
  }));

  console.log('Final results:', results.length, 'rows');
  console.log('Summary:', summary);
  console.log('=== END REPORT DEBUG ===');

  return {
    columns,
    rows: results,
    summary,
    totalRecords: results.length,
    executedAt: new Date().toISOString()
  };
}

/**
 * Apply filters to records
 */
function applyFilters(records: ListViewRecord[], filters: ReportFilter[]): ListViewRecord[] {
  if (!filters || filters.length === 0) return records;

  return records.filter(record => {
    return filters.every(filter => {
      const fieldValue = record[filter.field];

      switch (filter.operator) {
        case 'equals':
          return fieldValue === filter.value;

        case 'notEquals':
          return fieldValue !== filter.value;

        case 'contains':
          return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());

        case 'startsWith':
          return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());

        case 'greaterThan':
          return fieldValue > filter.value;

        case 'lessThan':
          return fieldValue < filter.value;

        case 'between':
          return fieldValue >= filter.value && fieldValue <= filter.value2;

        case 'in':
          return Array.isArray(filter.value) && filter.value.includes(fieldValue);

        default:
          return true;
      }
    });
  });
}

/**
 * Apply sorting to records
 */
function applySorting(records: ListViewRecord[], sortBy: ReportSort[]): ListViewRecord[] {
  return [...records].sort((a, b) => {
    for (const sort of sortBy) {
      const aVal = a[sort.field];
      const bVal = b[sort.field];

      if (aVal === bVal) continue;

      const comparison = aVal < bVal ? -1 : 1;
      return sort.direction === 'asc' ? comparison : -comparison;
    }
    return 0;
  });
}

/**
 * Apply grouping and aggregations
 */
function applyGrouping(
  records: ListViewRecord[],
  groupBy: string[],
  aggregations: ReportAggregation[]
): { rows: any[]; summary: ReportSummary } {
  // Group records
  const groups = new Map<string, ListViewRecord[]>();

  records.forEach(record => {
    const key = groupBy.map(field => record[field]).join('|');
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(record);
  });

  // Calculate aggregations for each group
  const rows: any[] = [];

  groups.forEach((groupRecords, key) => {
    const row: any = {};

    // Add group by fields
    groupBy.forEach((field, index) => {
      row[field] = key.split('|')[index];
    });

    // Calculate aggregations
    aggregations.forEach(agg => {
      row[agg.field] = calculateAggregation(groupRecords, agg.field, agg.function);
    });

    rows.push(row);
  });

  // Calculate overall summary
  const summary: ReportSummary = {};
  aggregations.forEach(agg => {
    summary[agg.field] = {
      label: agg.label,
      value: calculateAggregation(records, agg.field, agg.function),
      function: agg.function
    };
  });

  return { rows, summary };
}

/**
 * Calculate aggregation function on a field
 */
function calculateAggregation(
  records: ListViewRecord[],
  field: string,
  func: 'count' | 'sum' | 'avg' | 'min' | 'max'
): number {
  const values = records.map(r => r[field]).filter(v => v != null);

  switch (func) {
    case 'count':
      return records.length;

    case 'sum':
      return values.reduce((sum, val) => sum + Number(val), 0);

    case 'avg':
      if (values.length === 0) return 0;
      return values.reduce((sum, val) => sum + Number(val), 0) / values.length;

    case 'min':
      return Math.min(...values.map(Number));

    case 'max':
      return Math.max(...values.map(Number));

    default:
      return 0;
  }
}

/**
 * Calculate summary statistics
 */
function calculateSummary(
  records: ListViewRecord[],
  aggregations: ReportAggregation[]
): ReportSummary {
  const summary: ReportSummary = {};

  aggregations.forEach(agg => {
    summary[agg.field] = {
      label: agg.label,
      value: calculateAggregation(records, agg.field, agg.function),
      function: agg.function
    };
  });

  return summary;
}

/**
 * Format field label (convert camelCase to Title Case)
 */
function formatFieldLabel(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

/**
 * Infer field type from data
 */
function inferFieldType(field: string, records: ListViewRecord[]): string {
  if (records.length === 0) return 'text';

  const value = records[0][field];

  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (value instanceof Date) return 'date';

  // Check if it looks like a date string
  if (typeof value === 'string' && !isNaN(Date.parse(value))) {
    return 'date';
  }

  // Check if it looks like a currency
  if (typeof value === 'string' && value.match(/^\$[\d,]+(\.\d{2})?$/)) {
    return 'currency';
  }

  return 'text';
}

/**
 * Standard Reports Library
 */
export const STANDARD_REPORTS: ReportConfiguration[] = [
  {
    id: 'report_001',
    name: 'Sales Pipeline Report',
    objectName: 'Opportunity',
    selectedFields: ['stage', 'name', 'amount', 'probability', 'closeDate', 'clientName', 'owner'],
    groupBy: ['stage'],
    aggregations: [
      { field: 'amount', function: 'sum', label: 'Total Amount' },
      { field: 'id', function: 'count', label: 'Opportunity Count' }
    ],
    sortBy: [
      { field: 'amount', direction: 'desc' }
    ],
    chartType: 'bar'
  },
  {
    id: 'lead_conversion',
    name: 'Lead Conversion Rate',
    objectName: 'Lead',
    selectedFields: ['status', 'name', 'rating', 'leadSource', 'company', 'owner'],
    groupBy: ['status'],
    aggregations: [
      { field: 'id', function: 'count', label: 'Lead Count' }
    ],
    chartType: 'pie'
  },
  {
    id: 'case_resolution',
    name: 'Case Resolution Time',
    objectName: 'Case',
    selectedFields: ['status', 'priority', 'subject', 'clientName', 'createdDate', 'owner'],
    groupBy: ['status'],
    aggregations: [
      { field: 'id', function: 'count', label: 'Case Count' }
    ],
    chartType: 'bar'
  },
  {
    id: 'revenue_forecast',
    name: 'Revenue Forecast',
    objectName: 'Opportunity',
    selectedFields: ['closeDate', 'amount', 'probability', 'stage', 'name', 'clientName'],
    filters: [
      { field: 'stage', operator: 'in', value: ['Evaluation', 'Pitch', 'Discovery'] }
    ],
    sortBy: [
      { field: 'closeDate', direction: 'asc' }
    ],
    aggregations: [
      { field: 'amount', function: 'sum', label: 'Total Pipeline Value' },
      { field: 'amount', function: 'avg', label: 'Average Deal Size' },
      { field: 'id', function: 'count', label: 'Number of Opportunities' }
    ],
    chartType: 'table'
  },
  {
    id: 'activity_report',
    name: 'Activity Report',
    objectName: 'Task',
    selectedFields: ['status', 'priority', 'assignee', 'dueDate', 'title'],
    groupBy: ['status'],
    aggregations: [
      { field: 'id', function: 'count', label: 'Task Count' }
    ],
    chartType: 'bar'
  }
];
